import {
  createStylesParams,
  StyleParamType,
  wixColorParam,
  wixFontParam,
} from '@wix/tpa-settings';
import { StyleType } from '@wix/editor-platform-ooi-metadata-resolver';

export const DROPDOWN_STYLE = {
  BOX: 0,
  UNDERLINE: 1,
};

export type NavigationStylesParams = {
  // Navigation container
  navigationContainerBackgroundColor: StyleParamType.Color;
  navigationContainerPaddingHorizontal: StyleParamType.Number;
  navigationContainerPaddingVertical: StyleParamType.Number;
  navigationScrollArrowColor: StyleParamType.Color;
  navigationDividerColor: StyleParamType.Color;
  navigationContainerBorderTopColor: StyleParamType.Color;
  navigationContainerBorderTopWidth: StyleParamType.Number;
  navigationContainerBorderRightColor: StyleParamType.Color;
  navigationContainerBorderRightWidth: StyleParamType.Number;
  navigationContainerBorderBottomColor: StyleParamType.Color;
  navigationContainerBorderBottomWidth: StyleParamType.Number;
  navigationContainerBorderLeftColor: StyleParamType.Color;
  navigationContainerBorderLeftWidth: StyleParamType.Number;
  navigationContainerBorderLinked: StyleParamType.Number;
  navigationContainerCornerRadiusTopLeft: StyleParamType.Number;
  navigationContainerCornerRadiusTopRight: StyleParamType.Number;
  navigationContainerCornerRadiusBottomLeft: StyleParamType.Number;
  navigationContainerCornerRadiusBottomRight: StyleParamType.Number;
  navigationContainerCornerRadiusLinked: StyleParamType.Number;

  // Multi menu dropdown
  multiMenuDropdownStyle: StyleParamType.Number;
  // 1 = BOX, 0 = UNDERLINE. Navigation.st.css multiplies side border widths by this value.
  multiMenuDropdownIsBox: StyleParamType.Number;
  multiMenuDropdownFont: StyleParamType.Font;
  multiMenuDropdownTextColor: StyleParamType.Color;
  multiMenuDropdownBackgroundColor: StyleParamType.Color;
  multiMenuDropdownBorderColor: StyleParamType.Color;
  multiMenuDropdownBorderWidth: StyleParamType.Number;
  multiMenuDropdownCornerRadius: StyleParamType.Number;

  // Section navigation tabs - Default
  navTabDefaultFont: StyleParamType.Font;
  navTabDefaultTextColor: StyleParamType.Color;
  navTabDefaultBackgroundColor: StyleParamType.Color;
  navTabDefaultBorderTopColor: StyleParamType.Color;
  navTabDefaultBorderTopWidth: StyleParamType.Number;
  navTabDefaultBorderLinked: StyleParamType.Number;
  navTabDefaultBorderRightColor: StyleParamType.Color;
  navTabDefaultBorderRightWidth: StyleParamType.Number;
  navTabDefaultBorderBottomColor: StyleParamType.Color;
  navTabDefaultBorderBottomWidth: StyleParamType.Number;
  navTabDefaultBorderLeftColor: StyleParamType.Color;
  navTabDefaultBorderLeftWidth: StyleParamType.Number;
  navTabDefaultCornerRadiusTopLeft: StyleParamType.Number;
  navTabDefaultCornerRadiusTopRight: StyleParamType.Number;
  navTabDefaultCornerRadiusBottomLeft: StyleParamType.Number;
  navTabDefaultCornerRadiusBottomRight: StyleParamType.Number;
  navTabDefaultCornerRadiusLinked: StyleParamType.Number;

  // Section navigation tabs - Selected
  navTabSelectedFont: StyleParamType.Font;
  navTabSelectedTextColor: StyleParamType.Color;
  navTabSelectedBackgroundColor: StyleParamType.Color;
  navTabSelectedBorderTopColor: StyleParamType.Color;
  navTabSelectedBorderTopWidth: StyleParamType.Number;
  navTabSelectedBorderLinked: StyleParamType.Number;
  navTabSelectedBorderRightColor: StyleParamType.Color;
  navTabSelectedBorderRightWidth: StyleParamType.Number;
  navTabSelectedBorderBottomColor: StyleParamType.Color;
  navTabSelectedBorderBottomWidth: StyleParamType.Number;
  navTabSelectedBorderLeftColor: StyleParamType.Color;
  navTabSelectedBorderLeftWidth: StyleParamType.Number;
  navTabSelectedCornerRadiusTopLeft: StyleParamType.Number;
  navTabSelectedCornerRadiusTopRight: StyleParamType.Number;
  navTabSelectedCornerRadiusBottomLeft: StyleParamType.Number;
  navTabSelectedCornerRadiusBottomRight: StyleParamType.Number;
  navTabSelectedCornerRadiusLinked: StyleParamType.Number;
};

export const navigationStylesParams =
  createStylesParams<NavigationStylesParams>({
    // Navigation container
    navigationContainerBackgroundColor: {
      type: StyleParamType.Color,
      getDefaultValue: wixColorParam('color-1', 1),
      metadata: {
        type: StyleType.backgroundColor,
        defaultValue: { type: 'static', preset: 'color-1', opacity: 1 },
        ai: {
          description:
            'Background color of the section tab bar (horizontal strip inside the ordering widget for navigating between menu categories — not site navigation)',
        },
      },
    },
    navigationContainerPaddingHorizontal: {
      type: StyleParamType.Number,
      getDefaultValue: () => 0,
      metadata: {
        type: StyleType.number,
        defaultValue: { type: 'static', value: 0 },
        ai: {
          description:
            'Horizontal padding (px) inside the section tab bar container',
        },
      },
    },
    navigationContainerPaddingVertical: {
      type: StyleParamType.Number,
      getDefaultValue: () => 0,
      metadata: {
        type: StyleType.number,
        defaultValue: { type: 'static', value: 0 },
        ai: {
          description:
            'Vertical padding (px) inside the section tab bar container',
        },
      },
    },
    navigationScrollArrowColor: {
      type: StyleParamType.Color,
      getDefaultValue: wixColorParam('color-5', 1),
      metadata: {
        type: StyleType.color,
        defaultValue: { type: 'static', preset: 'color-5', opacity: 1 },
        ai: {
          description:
            'Color of the left/right scroll arrows that appear in the section tab bar when tabs overflow',
        },
      },
    },
    navigationDividerColor: {
      type: StyleParamType.Color,
      getDefaultValue: wixColorParam('color-5', 1),
      metadata: {
        type: StyleType.color,
        defaultValue: { type: 'static', preset: 'color-5', opacity: 1 },
        ai: {
          description:
            'Color of the divider line bordering the section tab bar',
        },
      },
    },
    navigationContainerBorderTopColor: {
      type: StyleParamType.Color,
      getDefaultValue: wixColorParam('color-5', 1),
      metadata: {
        type: StyleType.borderTopColor,
        defaultValue: { type: 'static', preset: 'color-5', opacity: 1 },
        ai: {
          description: 'Top border color of the section tab bar container',
        },
      },
    },
    navigationContainerBorderTopWidth: {
      type: StyleParamType.Number,
      getDefaultValue: () => 0,
      metadata: {
        type: StyleType.borderTopWidth,
        defaultValue: { type: 'static', value: 0 },
        ai: {
          description:
            'Top border thickness (px) of the section tab bar container',
        },
      },
    },
    navigationContainerBorderRightColor: {
      type: StyleParamType.Color,
      getDefaultValue: wixColorParam('color-5', 1),
      metadata: {
        type: StyleType.borderRightColor,
        defaultValue: { type: 'static', preset: 'color-5', opacity: 1 },
        ai: {
          description: 'Right border color of the section tab bar container',
        },
      },
    },
    navigationContainerBorderRightWidth: {
      type: StyleParamType.Number,
      getDefaultValue: () => 0,
      metadata: {
        type: StyleType.borderRightWidth,
        defaultValue: { type: 'static', value: 0 },
        ai: {
          description:
            'Right border thickness (px) of the section tab bar container',
        },
      },
    },
    navigationContainerBorderBottomColor: {
      type: StyleParamType.Color,
      getDefaultValue: wixColorParam('color-5', 1),
      metadata: {
        type: StyleType.borderBottomColor,
        defaultValue: { type: 'static', preset: 'color-5', opacity: 1 },
        ai: {
          description: 'Bottom border color of the section tab bar container',
        },
      },
    },
    navigationContainerBorderBottomWidth: {
      type: StyleParamType.Number,
      getDefaultValue: () => 0,
      metadata: {
        type: StyleType.borderBottomWidth,
        defaultValue: { type: 'static', value: 0 },
        ai: {
          description:
            'Bottom border thickness (px) of the section tab bar container',
        },
      },
    },
    navigationContainerBorderLeftColor: {
      type: StyleParamType.Color,
      getDefaultValue: wixColorParam('color-5', 1),
      metadata: {
        type: StyleType.borderLeftColor,
        defaultValue: { type: 'static', preset: 'color-5', opacity: 1 },
        ai: {
          description: 'Left border color of the section tab bar container',
        },
      },
    },
    navigationContainerBorderLeftWidth: {
      type: StyleParamType.Number,
      getDefaultValue: () => 0,
      metadata: {
        type: StyleType.borderLeftWidth,
        defaultValue: { type: 'static', value: 0 },
        ai: {
          description:
            'Left border thickness (px) of the section tab bar container',
        },
      },
    },
    navigationContainerBorderLinked: {
      type: StyleParamType.Number,
      getDefaultValue: () => 1,
      metadata: {
        type: StyleType.numberEnum,
        defaultValue: { type: 'static', value: 1 },
        ai: {
          description:
            '1 = all four border widths of the section tab bar container locked together, 0 = each side independent',
        },
      },
    },
    navigationContainerCornerRadiusTopLeft: {
      type: StyleParamType.Number,
      getDefaultValue: () => 0,
      metadata: {
        type: StyleType.borderTopLeftRadius,
        defaultValue: { type: 'static', value: 0 },
        ai: {
          description:
            'Top-left corner radius (px) of the section tab bar container',
        },
      },
    },
    navigationContainerCornerRadiusTopRight: {
      type: StyleParamType.Number,
      getDefaultValue: () => 0,
      metadata: {
        type: StyleType.borderTopRightRadius,
        defaultValue: { type: 'static', value: 0 },
        ai: {
          description:
            'Top-right corner radius (px) of the section tab bar container',
        },
      },
    },
    navigationContainerCornerRadiusBottomLeft: {
      type: StyleParamType.Number,
      getDefaultValue: () => 0,
      metadata: {
        type: StyleType.borderBottomLeftRadius,
        defaultValue: { type: 'static', value: 0 },
        ai: {
          description:
            'Bottom-left corner radius (px) of the section tab bar container',
        },
      },
    },
    navigationContainerCornerRadiusBottomRight: {
      type: StyleParamType.Number,
      getDefaultValue: () => 0,
      metadata: {
        type: StyleType.borderBottomRightRadius,
        defaultValue: { type: 'static', value: 0 },
        ai: {
          description:
            'Bottom-right corner radius (px) of the section tab bar container',
        },
      },
    },
    navigationContainerCornerRadiusLinked: {
      type: StyleParamType.Number,
      getDefaultValue: () => 1,
      metadata: {
        type: StyleType.numberEnum,
        defaultValue: { type: 'static', value: 1 },
        ai: {
          description:
            '1 = all section tab bar container corner radii locked together, 0 = each corner independent',
        },
      },
    },

    // Multi menu dropdown
    multiMenuDropdownStyle: {
      type: StyleParamType.Number,
      getDefaultValue: () => DROPDOWN_STYLE.BOX,
      metadata: {
        type: StyleType.numberEnum,
        defaultValue: { type: 'static', value: DROPDOWN_STYLE.BOX },
        ai: {
          description:
            'Controls the visual style of the multi-menu dropdown — 0 = box style with visible border, 1 = underline style',
        },
      },
    },
    multiMenuDropdownIsBox: {
      type: StyleParamType.Number,
      getDefaultValue: () => 1,
      metadata: {
        type: StyleType.numberEnum,
        defaultValue: { type: 'static', value: 1 },
        ai: {
          description:
            'CSS multiplier flag mirroring multiMenuDropdownStyle — 0 = underline (no side borders), 1 = box (side borders visible)',
        },
      },
    },
    multiMenuDropdownFont: {
      type: StyleParamType.Font,
      getDefaultValue: (params) =>
        wixFontParam('Body-M', {
          htmlTag: 'p',
          size: params.isMobile ? 14 : 16,
        })(params),
      metadata: {
        type: StyleType.font,
        defaultValue: { type: 'static', preset: 'Body-M', size: 16 },
        mobileDefaultValue: { type: 'static', preset: 'Body-M', size: 14 },
        ai: {
          description:
            'Font for the multi-menu selector dropdown (shown when the restaurant has multiple menus, e.g. "Lunch Menu", "Dinner Menu")',
        },
      },
    },
    multiMenuDropdownTextColor: {
      type: StyleParamType.Color,
      getDefaultValue: wixColorParam('color-5', 1),
      metadata: {
        type: StyleType.color,
        defaultValue: { type: 'static', preset: 'color-5', opacity: 1 },
        ai: {
          description: 'Text color for the multi-menu selector dropdown',
        },
      },
    },
    multiMenuDropdownBackgroundColor: {
      type: StyleParamType.Color,
      getDefaultValue: wixColorParam('color-1', 1),
      metadata: {
        type: StyleType.backgroundColor,
        defaultValue: { type: 'static', preset: 'color-1', opacity: 1 },
        ai: {
          description: 'Background color for the multi-menu selector dropdown',
        },
      },
    },
    multiMenuDropdownBorderColor: {
      type: StyleParamType.Color,
      getDefaultValue: wixColorParam('color-5', 1),
      metadata: {
        type: StyleType.borderColor,
        defaultValue: { type: 'static', preset: 'color-5', opacity: 1 },
        ai: {
          description: 'Border color for the multi-menu selector dropdown',
        },
      },
    },
    multiMenuDropdownBorderWidth: {
      type: StyleParamType.Number,
      getDefaultValue: () => 1,
      metadata: {
        type: StyleType.borderWidth,
        defaultValue: { type: 'static', value: 1 },
        ai: {
          description:
            'Border thickness (px) for the multi-menu selector dropdown',
        },
      },
    },
    multiMenuDropdownCornerRadius: {
      type: StyleParamType.Number,
      getDefaultValue: () => 0,
      metadata: {
        type: StyleType.borderRadius,
        defaultValue: { type: 'static', value: 0 },
        ai: {
          description:
            'Corner radius (px) for the multi-menu selector dropdown',
        },
      },
    },

    // Section navigation tabs - Default
    navTabDefaultFont: {
      type: StyleParamType.Font,
      getDefaultValue: (params) =>
        wixFontParam('Body-M', {
          htmlTag: 'p',
          size: params.isMobile ? 14 : 16,
        })(params),
      metadata: {
        type: StyleType.font,
        defaultValue: { type: 'static', preset: 'Body-M', size: 16 },
        mobileDefaultValue: { type: 'static', preset: 'Body-M', size: 14 },
        ai: {
          description:
            "Font for the menu category tabs — the clickable row of tabs that jump between menu sections (e.g. Starters, Mains, Desserts). This is menu content, not site navigation; it is part of the menu's visual theme and should follow heading/title styling requests for the menu.",
        },
      },
    },
    navTabDefaultTextColor: {
      type: StyleParamType.Color,
      getDefaultValue: wixColorParam('color-5', 1),
      metadata: {
        type: StyleType.color,
        defaultValue: { type: 'static', preset: 'color-5', opacity: 1 },
        ai: {
          description:
            'Label color of an unselected menu category tab (the clickable tabs that switch between menu sections). A menu surface — should follow the overall menu color theme.',
        },
      },
    },
    navTabDefaultBackgroundColor: {
      type: StyleParamType.Color,
      getDefaultValue: wixColorParam('color-1', 1),
      metadata: {
        type: StyleType.backgroundColor,
        defaultValue: { type: 'static', preset: 'color-1', opacity: 1 },
        ai: {
          description:
            'Background fill of an unselected menu category tab — a menu surface that should follow the overall menu background/theme.',
        },
      },
    },
    navTabDefaultBorderTopColor: {
      type: StyleParamType.Color,
      getDefaultValue: wixColorParam('color-5', 1),
      metadata: {
        type: StyleType.borderTopColor,
        defaultValue: { type: 'static', preset: 'color-5', opacity: 1 },
        ai: {
          description:
            'Top border color of an unselected section navigation tab',
        },
      },
    },
    navTabDefaultBorderTopWidth: {
      type: StyleParamType.Number,
      getDefaultValue: () => 0,
      metadata: {
        type: StyleType.borderTopWidth,
        defaultValue: { type: 'static', value: 0 },
        ai: {
          description:
            'Top border thickness (px) of an unselected section navigation tab',
        },
      },
    },
    navTabDefaultBorderLinked: {
      type: StyleParamType.Number,
      getDefaultValue: () => 1,
      metadata: {
        type: StyleType.numberEnum,
        defaultValue: { type: 'static', value: 1 },
        ai: {
          description:
            '1 = all four border widths of an unselected section tab locked together, 0 = each side independent',
        },
      },
    },
    navTabDefaultBorderRightColor: {
      type: StyleParamType.Color,
      getDefaultValue: wixColorParam('color-5', 1),
      metadata: {
        type: StyleType.borderRightColor,
        defaultValue: { type: 'static', preset: 'color-5', opacity: 1 },
        ai: {
          description:
            'Right border color of an unselected section navigation tab',
        },
      },
    },
    navTabDefaultBorderRightWidth: {
      type: StyleParamType.Number,
      getDefaultValue: () => 0,
      metadata: {
        type: StyleType.borderRightWidth,
        defaultValue: { type: 'static', value: 0 },
        ai: {
          description:
            'Right border thickness (px) of an unselected section navigation tab',
        },
      },
    },
    navTabDefaultBorderBottomColor: {
      type: StyleParamType.Color,
      getDefaultValue: wixColorParam('color-5', 1),
      metadata: {
        type: StyleType.borderBottomColor,
        defaultValue: { type: 'static', preset: 'color-5', opacity: 1 },
        ai: {
          description:
            'Bottom border color of an unselected section navigation tab',
        },
      },
    },
    navTabDefaultBorderBottomWidth: {
      type: StyleParamType.Number,
      getDefaultValue: () => 0,
      metadata: {
        type: StyleType.borderBottomWidth,
        defaultValue: { type: 'static', value: 0 },
        ai: {
          description:
            'Bottom border thickness (px) of an unselected section navigation tab',
        },
      },
    },
    navTabDefaultBorderLeftColor: {
      type: StyleParamType.Color,
      getDefaultValue: wixColorParam('color-5', 1),
      metadata: {
        type: StyleType.borderLeftColor,
        defaultValue: { type: 'static', preset: 'color-5', opacity: 1 },
        ai: {
          description:
            'Left border color of an unselected section navigation tab',
        },
      },
    },
    navTabDefaultBorderLeftWidth: {
      type: StyleParamType.Number,
      getDefaultValue: () => 0,
      metadata: {
        type: StyleType.borderLeftWidth,
        defaultValue: { type: 'static', value: 0 },
        ai: {
          description:
            'Left border thickness (px) of an unselected section navigation tab',
        },
      },
    },
    navTabDefaultCornerRadiusTopLeft: {
      type: StyleParamType.Number,
      getDefaultValue: () => 0,
      metadata: {
        type: StyleType.borderTopLeftRadius,
        defaultValue: { type: 'static', value: 0 },
        ai: {
          description:
            'Top-left corner radius (px) of an unselected section navigation tab',
        },
      },
    },
    navTabDefaultCornerRadiusTopRight: {
      type: StyleParamType.Number,
      getDefaultValue: () => 0,
      metadata: {
        type: StyleType.borderTopRightRadius,
        defaultValue: { type: 'static', value: 0 },
        ai: {
          description:
            'Top-right corner radius (px) of an unselected section navigation tab',
        },
      },
    },
    navTabDefaultCornerRadiusBottomLeft: {
      type: StyleParamType.Number,
      getDefaultValue: () => 0,
      metadata: {
        type: StyleType.borderBottomLeftRadius,
        defaultValue: { type: 'static', value: 0 },
        ai: {
          description:
            'Bottom-left corner radius (px) of an unselected section navigation tab',
        },
      },
    },
    navTabDefaultCornerRadiusBottomRight: {
      type: StyleParamType.Number,
      getDefaultValue: () => 0,
      metadata: {
        type: StyleType.borderBottomRightRadius,
        defaultValue: { type: 'static', value: 0 },
        ai: {
          description:
            'Bottom-right corner radius (px) of an unselected section navigation tab',
        },
      },
    },
    navTabDefaultCornerRadiusLinked: {
      type: StyleParamType.Number,
      getDefaultValue: () => 1,
      metadata: {
        type: StyleType.numberEnum,
        defaultValue: { type: 'static', value: 1 },
        ai: {
          description:
            '1 = all corner radii of an unselected section tab locked together, 0 = each corner independent',
        },
      },
    },

    // Section navigation tabs - Selected
    navTabSelectedFont: {
      type: StyleParamType.Font,
      getDefaultValue: (params) =>
        wixFontParam('Body-M', {
          htmlTag: 'p',
          size: params.isMobile ? 14 : 16,
        })(params),
      metadata: {
        type: StyleType.font,
        defaultValue: { type: 'static', preset: 'Body-M', size: 16 },
        mobileDefaultValue: { type: 'static', preset: 'Body-M', size: 14 },
        ai: {
          description:
            "Font for the active menu category tab (the currently open menu section). Part of the menu's visual theme; follow heading/title styling requests for the menu.",
        },
      },
    },
    navTabSelectedTextColor: {
      type: StyleParamType.Color,
      getDefaultValue: wixColorParam('color-5', 1),
      metadata: {
        type: StyleType.color,
        defaultValue: { type: 'static', preset: 'color-5', opacity: 1 },
        ai: {
          description:
            'Label color of the active/selected menu category tab. Often set to an accent color to highlight the open section.',
        },
      },
    },
    navTabSelectedBackgroundColor: {
      type: StyleParamType.Color,
      getDefaultValue: wixColorParam('color-1', 1),
      metadata: {
        type: StyleType.backgroundColor,
        defaultValue: { type: 'static', preset: 'color-1', opacity: 1 },
        ai: {
          description:
            'Background fill of the active/selected menu category tab — a menu surface; can be given an accent or contrasting fill to mark the open section.',
        },
      },
    },
    navTabSelectedBorderTopColor: {
      type: StyleParamType.Color,
      getDefaultValue: wixColorParam('color-5', 1),
      metadata: {
        type: StyleType.borderTopColor,
        defaultValue: { type: 'static', preset: 'color-5', opacity: 1 },
        ai: {
          description: 'Top border color of the active section navigation tab',
        },
      },
    },
    navTabSelectedBorderTopWidth: {
      type: StyleParamType.Number,
      getDefaultValue: () => 0,
      metadata: {
        type: StyleType.borderTopWidth,
        defaultValue: { type: 'static', value: 0 },
        ai: {
          description:
            'Top border thickness (px) of the active section navigation tab',
        },
      },
    },
    navTabSelectedBorderLinked: {
      type: StyleParamType.Number,
      getDefaultValue: () => 0,
      metadata: {
        type: StyleType.numberEnum,
        defaultValue: { type: 'static', value: 0 },
        ai: {
          description:
            '1 = all four border widths of the active section tab locked together, 0 = each side independent (default is 0 so the bottom underline indicator can differ)',
        },
      },
    },
    navTabSelectedBorderRightColor: {
      type: StyleParamType.Color,
      getDefaultValue: wixColorParam('color-5', 1),
      metadata: {
        type: StyleType.borderRightColor,
        defaultValue: { type: 'static', preset: 'color-5', opacity: 1 },
        ai: {
          description:
            'Right border color of the active section navigation tab',
        },
      },
    },
    navTabSelectedBorderRightWidth: {
      type: StyleParamType.Number,
      getDefaultValue: () => 0,
      metadata: {
        type: StyleType.borderRightWidth,
        defaultValue: { type: 'static', value: 0 },
        ai: {
          description:
            'Right border thickness (px) of the active section navigation tab',
        },
      },
    },
    navTabSelectedBorderBottomColor: {
      type: StyleParamType.Color,
      getDefaultValue: wixColorParam('color-8', 1),
      metadata: {
        type: StyleType.borderBottomColor,
        defaultValue: { type: 'static', preset: 'color-8', opacity: 1 },
        ai: {
          description:
            'The underline accent under the active menu category tab (the colored bar marking the open section). Defaults to the accent color — a primary target for accent-color styling requests.',
        },
      },
    },
    navTabSelectedBorderBottomWidth: {
      type: StyleParamType.Number,
      getDefaultValue: () => 2,
      metadata: {
        type: StyleType.borderBottomWidth,
        defaultValue: { type: 'static', value: 2 },
        ai: {
          description:
            'Bottom border thickness (px) of the active section tab (this is the active-state underline indicator, defaults to 2px)',
        },
      },
    },
    navTabSelectedBorderLeftColor: {
      type: StyleParamType.Color,
      getDefaultValue: wixColorParam('color-5', 1),
      metadata: {
        type: StyleType.borderLeftColor,
        defaultValue: { type: 'static', preset: 'color-5', opacity: 1 },
        ai: {
          description: 'Left border color of the active section navigation tab',
        },
      },
    },
    navTabSelectedBorderLeftWidth: {
      type: StyleParamType.Number,
      getDefaultValue: () => 0,
      metadata: {
        type: StyleType.borderLeftWidth,
        defaultValue: { type: 'static', value: 0 },
        ai: {
          description:
            'Left border thickness (px) of the active section navigation tab',
        },
      },
    },
    navTabSelectedCornerRadiusTopLeft: {
      type: StyleParamType.Number,
      getDefaultValue: () => 0,
      metadata: {
        type: StyleType.borderTopLeftRadius,
        defaultValue: { type: 'static', value: 0 },
        ai: {
          description:
            'Top-left corner radius (px) of the active section navigation tab',
        },
      },
    },
    navTabSelectedCornerRadiusTopRight: {
      type: StyleParamType.Number,
      getDefaultValue: () => 0,
      metadata: {
        type: StyleType.borderTopRightRadius,
        defaultValue: { type: 'static', value: 0 },
        ai: {
          description:
            'Top-right corner radius (px) of the active section navigation tab',
        },
      },
    },
    navTabSelectedCornerRadiusBottomLeft: {
      type: StyleParamType.Number,
      getDefaultValue: () => 0,
      metadata: {
        type: StyleType.borderBottomLeftRadius,
        defaultValue: { type: 'static', value: 0 },
        ai: {
          description:
            'Bottom-left corner radius (px) of the active section navigation tab',
        },
      },
    },
    navTabSelectedCornerRadiusBottomRight: {
      type: StyleParamType.Number,
      getDefaultValue: () => 0,
      metadata: {
        type: StyleType.borderBottomRightRadius,
        defaultValue: { type: 'static', value: 0 },
        ai: {
          description:
            'Bottom-right corner radius (px) of the active section navigation tab',
        },
      },
    },
    navTabSelectedCornerRadiusLinked: {
      type: StyleParamType.Number,
      getDefaultValue: () => 1,
      metadata: {
        type: StyleType.numberEnum,
        defaultValue: { type: 'static', value: 1 },
        ai: {
          description:
            '1 = all corner radii of the active section tab locked together, 0 = each corner independent',
        },
      },
    },
  });
